import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { toolbarPlugin } from "@react-pdf-viewer/toolbar";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/toolbar/lib/styles/index.css";
import "@react-pdf-viewer/zoom/lib/styles/index.css";
import "@react-pdf-viewer/page-navigation/lib/styles/index.css";
import intl from "react-intl-universal";
import Axios from "../config/Axios";
import { fnIsPresignedS3Url, fnShouldUseAuthenticatedRequest } from "../utils/urlRequestStrategy";

const DEFAULT_FRONT_PREVIEW_TIMEOUT_MS = Number(process.env.REACT_APP_FRONT_PREVIEW_TIMEOUT_MS || 40000);

const PdfFireViewer = ({
  pdfBlob,
  canDownload,
  nameFile,
  downloadUrl,
  downloadFileName,
  timeoutMs,
  downloadMode,
  fallbackMessage,
  showRetry,
  onRetry,
  onFallbackShown,
  fallbackReason,
  cViewerHeight,
  className,
}) => {
  const fileUrlRef = useRef(null);
  const viewerRef = useRef(null);
  const urlWasCreated = useRef(false);
  const timeoutRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fallbackState, setFallbackState] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);

  const toolbarPluginInstance = useRef(toolbarPlugin()).current;
  const { Toolbar } = toolbarPluginInstance;
  const zoomPluginInstance = useRef(zoomPlugin()).current;
  const pageNavigationPluginInstance = useRef(pageNavigationPlugin()).current;

  const clearPreviewTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const fnGetFallbackDownloadUrl = () => {
    if (downloadUrl) return downloadUrl;
    if (fallbackState?.downloadUrl) return fallbackState.downloadUrl;
    if (typeof pdfBlob === "string" && (pdfBlob.startsWith("http") || pdfBlob.startsWith("/"))) {
      return pdfBlob;
    }
    return "";
  };

  const fnGetFallbackFileName = () => {
    if (downloadFileName) return downloadFileName;
    if (fallbackState?.fileName) return fallbackState.fileName;
    if (nameFile) return nameFile;
    return "documento.pdf";
  };

  const fnGetFallbackMessage = () => {
    if (fallbackMessage) return fallbackMessage;

    const cReason = fallbackState?.reason || fallbackReason;
    switch (cReason) {
      case "too_large":
        return intl.get("ViewPDF.unable_to_preview.too_large");
      case "timeout":
        return intl.get("ViewPDF.unable_to_preview.timeout");
      case "busy":
        return intl.get("ViewPDF.unable_to_preview.busy");
      case "error":
      default:
        return intl.get("ViewPDF.unable_to_preview.generic");
    }
  };

  const fnShowFallback = (cReason = "error", oData = {}) => {
    setFallbackState((oPrev) => {
      if (oPrev) return oPrev;
      const oNext = {
        reason: cReason,
        downloadUrl: oData.downloadUrl || "",
        fileName: oData.fileName || "",
      };
      if (typeof onFallbackShown === "function") {
        try {
          onFallbackShown(cReason);
        } catch (e) {
          // no-op
        }
      }
      return oNext;
    });
    setIsLoading(false);
  };

  useEffect(() => {
    let bIsMounted = true;

    const loadPdf = async () => {
      clearPreviewTimer();
      setIsLoading(true);
      setError(null);
      setFallbackState(null);

      if (urlWasCreated.current && fileUrlRef.current) {
        URL.revokeObjectURL(fileUrlRef.current);
        fileUrlRef.current = null;
        urlWasCreated.current = false;
      }

      if (fallbackReason) {
        if (!bIsMounted) return;
        fnShowFallback(fallbackReason, {
          downloadUrl: downloadUrl || "",
          fileName: downloadFileName || nameFile,
        });
        return;
      }

      timeoutRef.current = setTimeout(() => {
        fnShowFallback("timeout", {
          downloadUrl,
          fileName: downloadFileName || nameFile,
        });
      }, Number(timeoutMs || DEFAULT_FRONT_PREVIEW_TIMEOUT_MS));

      try {
        if (typeof pdfBlob === "string" && pdfBlob.startsWith("data:")) {
          fileUrlRef.current = pdfBlob;
        } else if (typeof pdfBlob === "string" && (pdfBlob.startsWith("http") || pdfBlob.startsWith("/"))) {
          const bUseAuthRequest = fnShouldUseAuthenticatedRequest(pdfBlob) && !fnIsPresignedS3Url(pdfBlob);
          if (bUseAuthRequest) {
            const oResponse = await Axios.get(pdfBlob, {
              responseType: "blob",
              timeout: Number(timeoutMs || DEFAULT_FRONT_PREVIEW_TIMEOUT_MS),
            });
            fileUrlRef.current = URL.createObjectURL(oResponse.data);
          } else {
            const oBlob = await fnFetchBlobWithoutAuth(pdfBlob, timeoutMs);
            fileUrlRef.current = URL.createObjectURL(oBlob);
          }
          urlWasCreated.current = true;
        } else if (typeof pdfBlob === "string" && pdfBlob.startsWith("blob:")) {
          fileUrlRef.current = pdfBlob;
        } else if (pdfBlob instanceof Blob || pdfBlob instanceof File) {
          fileUrlRef.current = URL.createObjectURL(pdfBlob);
          urlWasCreated.current = true;
        } else {
          throw new Error("Formato de PDF não suportado");
        }

        if (!bIsMounted) return;
        setIsLoading(false);
      } catch (err) {
        if (!bIsMounted) return;
        setError(err?.message || "Erro ao carregar PDF");
        fnShowFallback("error", {
          downloadUrl,
          fileName: downloadFileName || nameFile,
        });
      }
    };

    loadPdf();

    return () => {
      bIsMounted = false;
      clearPreviewTimer();
      if (urlWasCreated.current && fileUrlRef.current) {
        URL.revokeObjectURL(fileUrlRef.current);
        fileUrlRef.current = null;
        urlWasCreated.current = false;
      }
    };
  }, [pdfBlob, timeoutMs, downloadUrl, downloadFileName, nameFile, fallbackReason]);

  const fnDownloadBlobByResponse = (oBlob, cFilename) => {
    const cBlobUrl = URL.createObjectURL(oBlob);
    const elLink = document.createElement("a");
    elLink.href = cBlobUrl;
    elLink.download = cFilename;
    document.body.appendChild(elLink);
    elLink.click();
    document.body.removeChild(elLink);
    URL.revokeObjectURL(cBlobUrl);
  };

  const fnEnsurePdfFileName = (cOriginalName) => {
    const cSafeName = (typeof cOriginalName === "string" && cOriginalName.trim()) ? cOriginalName.trim() : "documento.pdf";
    if (cSafeName.toLowerCase().endsWith(".pdf")) return cSafeName;
    const nLastDot = cSafeName.lastIndexOf(".");
    const cBaseName = nLastDot > 0 ? cSafeName.slice(0, nLastDot) : cSafeName;
    return `${cBaseName || "documento"}.pdf`;
  };

  const fnFetchBlobWithoutAuth = async (cUrl, nTimeoutMs) => {
    const oController = new AbortController();
    const timeoutId = setTimeout(() => oController.abort(), Number(nTimeoutMs || DEFAULT_FRONT_PREVIEW_TIMEOUT_MS));

    try {
      const oResponse = await fetch(cUrl, {
        method: "GET",
        credentials: "omit",
        redirect: "follow",
        signal: oController.signal,
      });
      if (!oResponse.ok) throw new Error("Falha ao carregar arquivo");
      return oResponse.blob();
    } finally {
      clearTimeout(timeoutId);
    }
  };

  const downloadBlob = async (cUrl, cFilename = "documento.pdf") => {
    if (!cUrl || typeof cUrl !== "string") return;

    try {
      const bUseAuthRequest = fnShouldUseAuthenticatedRequest(cUrl) && !fnIsPresignedS3Url(cUrl);
      const cMode = downloadMode === "auto"
        ? ((cUrl.startsWith("blob:") || cUrl.startsWith("data:") || !bUseAuthRequest) ? "direct" : "axios")
        : downloadMode;

      if (cMode === "direct") {
        const fBlob = await fnFetchBlobWithoutAuth(cUrl, timeoutMs);
        fnDownloadBlobByResponse(fBlob, cFilename);
        return;
      }

      const oResponse = await Axios.get(cUrl, { responseType: "blob" });
      fnDownloadBlobByResponse(oResponse.data, cFilename);
    } catch (oError) {
      setError(intl.get("ViewPDF.error_loading_document") || "Erro ao baixar arquivo");
    }
  };

  const getFileName = (cOriginalName) => {
    if (typeof cOriginalName === "string" && cOriginalName.trim() !== "") return cOriginalName;
    return "documento.pdf";
  };

  const fnDownloadPreviewPdf = async () => {
    const cPreviewFilename = fnEnsurePdfFileName(nameFile || downloadFileName);

    try {
      if (fileUrlRef.current) {
        await downloadBlob(fileUrlRef.current, cPreviewFilename);
        return;
      }

      if (pdfBlob instanceof Blob || pdfBlob instanceof File) {
        fnDownloadBlobByResponse(pdfBlob, cPreviewFilename);
        return;
      }

      if (typeof pdfBlob === "string" && pdfBlob) {
        await downloadBlob(pdfBlob, cPreviewFilename);
      }
    } catch (oError) {
      setError(intl.get("ViewPDF.error_loading_document") || "Erro ao baixar arquivo");
    }
  };

  const fnHandleRetry = async () => {
    if (!showRetry || typeof onRetry !== "function" || isRetrying) return;
    setIsRetrying(true);
    try {
      await Promise.resolve(onRetry());
    } finally {
      setIsRetrying(false);
    }
  };

  if (fallbackState) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <p style={{ marginBottom: "12px" }}>
          {fnGetFallbackMessage()}
        </p>
        {canDownload && !!fnGetFallbackDownloadUrl() && (
          <button
            onClick={() => downloadBlob(fnGetFallbackDownloadUrl(), getFileName(fnGetFallbackFileName()))}
            style={{
              marginRight: "8px",
              padding: "6px 12px",
              borderRadius: "5px",
              border: "1px solid #ccc",
              backgroundColor: "#ffffff",
              cursor: "pointer",
            }}
          >
            {intl.get("ViewPDF.download_file")}
          </button>
        )}
        {showRetry && typeof onRetry === "function" && (
          <button
            onClick={fnHandleRetry}
            disabled={isRetrying}
            style={{
              padding: "6px 12px",
              borderRadius: "5px",
              border: "1px solid #ccc",
              backgroundColor: "#ffffff",
              cursor: isRetrying ? "not-allowed" : "pointer",
              opacity: isRetrying ? 0.7 : 1,
            }}
          >
            {intl.get("ViewPDF.retry")}
          </button>
        )}
      </div>
    );
  }

  if (isLoading) {
    return <div style={{ textAlign: "center", padding: "20px" }}>Carregando PDF...</div>;
  }

  if (error && !fileUrlRef.current) {
    return <div style={{ color: "red", textAlign: "center", padding: "20px" }}>{error}</div>;
  }

  return fileUrlRef.current ? (
    <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.10.377/build/pdf.worker.min.js">
      <div
        className={className}
        style={{
          border: "1px solid #dcdcdc",
          borderRadius: "8px",
          backgroundColor: "#f9f9f9",
          padding: "10px",
          height: cViewerHeight,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "#ececec",
            padding: "8px",
            borderRadius: "5px",
            marginBottom: "8px",
            justifyContent: "space-between",
          }}
        >
          <Toolbar>
            {(props) => {
              const {
                CurrentPageInput,
                GoToNextPage,
                GoToPreviousPage,
                NumberOfPages,
                Zoom,
                ZoomIn,
                ZoomOut,
              } = props;
              return (
                <>
                  <div style={{ padding: "0px 2px" }}>
                    <ZoomOut />
                  </div>
                  <div style={{ padding: "0px 2px" }}>
                    <Zoom />
                  </div>
                  <div style={{ padding: "0px 2px" }}>
                    <ZoomIn />
                  </div>
                  <div style={{ padding: "0px 2px", marginLeft: "auto" }}>
                    <GoToPreviousPage />
                  </div>
                  <div style={{ padding: "0px 2px", width: "100px" }}>
                    <CurrentPageInput /> / <NumberOfPages />
                  </div>
                  <div style={{ padding: "0px 2px" }}>
                    <GoToNextPage />
                  </div>
                </>
              );
            }}
          </Toolbar>

          {canDownload && (
            <button
              onClick={fnDownloadPreviewPdf}
              style={{
                marginLeft: "10px",
                padding: "5px 10px",
                borderRadius: "5px",
                border: "1px solid #ccc",
                backgroundColor: "#ffffff",
                cursor: "pointer",
              }}
            >
              {intl.get("ViewPDF.download_pdf")}
            </button>
          )}
        </div>

        <div
          style={{
            flex: 1,
            overflow: "auto",
            border: "1px solid #e0e0e0",
            padding: "5px",
            borderRadius: "4px",
          }}
        >
          <style>
            {`
              .rpv-core__textbox {
                width: 50% !important;
                display: inline-block !important;
                padding: 4px 6px !important;
                font-size: 14px !important;
                height: auto !important;
                box-sizing: border-box !important;
              }
            `}
          </style>
          <Viewer
            ref={viewerRef}
            fileUrl={fileUrlRef.current}
            onDocumentLoad={() => {
              clearPreviewTimer();
            }}
            renderError={() => {
              fnShowFallback("error", {
                downloadUrl: fnGetFallbackDownloadUrl(),
                fileName: fnGetFallbackFileName(),
              });
              return <div />;
            }}
            plugins={[
              toolbarPluginInstance,
              zoomPluginInstance,
              pageNavigationPluginInstance,
            ]}
          />
        </div>
      </div>
    </Worker>
  ) : (
    <p>{intl.get("ViewPDF.loading")}</p>
  );
};

PdfFireViewer.propTypes = {
  pdfBlob: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]).isRequired,
  canDownload: PropTypes.bool,
  nameFile: PropTypes.string,
  downloadUrl: PropTypes.string,
  downloadFileName: PropTypes.string,
  timeoutMs: PropTypes.number,
  downloadMode: PropTypes.oneOf(["auto", "axios", "direct"]),
  fallbackMessage: PropTypes.string,
  showRetry: PropTypes.bool,
  onRetry: PropTypes.func,
  onFallbackShown: PropTypes.func,
  fallbackReason: PropTypes.oneOf(["too_large", "busy", "timeout", "error"]),
  cViewerHeight: PropTypes.string,
  className: PropTypes.string,
};

PdfFireViewer.defaultProps = {
  canDownload: false,
  nameFile: "documento.pdf",
  downloadUrl: "",
  downloadFileName: "",
  timeoutMs: DEFAULT_FRONT_PREVIEW_TIMEOUT_MS,
  downloadMode: "auto",
  fallbackMessage: "",
  showRetry: false,
  onRetry: null,
  onFallbackShown: null,
  fallbackReason: null,
  cViewerHeight: "70vh",
  className: "",
};

export default PdfFireViewer;
